import Length from "./Length";
import Rectangle from "./Rectangle";
import Area from "./Area";
import AbstractShape from "./AbstractShape";

export default class RoundedRectangle extends AbstractShape {
	static NAME = "rounded-rectangle";

	constructor( length, width, radius, top_left_radius, top_right_radius, bottom_left_radius, bottom_right_radius ) {
		super();

		this.length = length;
		this.width = width;
		this.radius = radius;
		this.top_right_radius = top_right_radius;
		this.bottom_right_radius = bottom_right_radius;
		this.bottom_left_radius = bottom_left_radius;
		this.top_left_radius = top_left_radius;
	}

	get_name() {
		return RoundedRectangle.NAME;
	}

	get_enclosing_rectangle() {
		return new Rectangle( this.length, this.width );
	}

	get_area() //A = 2 r (a + b) + a b + π r^2
	{
		if( this.is_individual_radius() ) {
			const width = this.length.in_mm();
			const height = this.width.in_mm();
			const totalRectangleArea = width * height;

			const topRightCircleArea = Math.PI * Math.pow( this.top_right_radius.in_mm(), 2 );
			const topRightRectangleArea = ( this.top_right_radius.in_mm() * 2 ) * ( this.top_right_radius.in_mm() * 2 );

			const bottomRightCircleArea = Math.PI * Math.pow( this.bottom_right_radius.in_mm(), 2 );
			const bottomRightRectangleArea = ( this.bottom_right_radius.in_mm() * 2 ) * ( this.bottom_right_radius.in_mm() * 2 );

			const bottomLeftCircleArea = Math.PI * Math.pow( this.bottom_left_radius.in_mm(), 2 );
			const bottomLeftRectangleArea = ( this.bottom_left_radius.in_mm() * 2 ) * ( this.bottom_left_radius.in_mm() * 2 );

			const topLeftCircleArea = Math.PI * Math.pow( this.top_left_radius.in_mm(), 2 );
			const topLeftRectangleArea = ( this.top_left_radius.in_mm() * 2 ) * ( this.top_left_radius.in_mm() * 2 );

			const topRightCornerArea = ( topRightCircleArea - topRightRectangleArea ) / 4;
			const bottomRightCornerArea = ( bottomRightCircleArea - bottomRightRectangleArea ) / 4;
			const bottomLeftCornerArea = ( bottomLeftCircleArea - bottomLeftRectangleArea ) / 4;
			const topLeftCornerArea = ( topLeftCircleArea - topLeftRectangleArea ) / 4;

			const area = totalRectangleArea - topRightCornerArea - bottomRightCornerArea - bottomLeftCornerArea - topLeftCornerArea;

			return new Area( area );
		}

		const a = this.length.in_mm();
		const b = this.width.in_mm();
		const r = this.radius.in_mm();
		return new Area( 2 * r * ( a + b ) + a * b + Math.PI * r * r );
	}

	get_circumference() {
		if( this.is_individual_radius() ) {
			const width = this.length.in_mm();
			const height = this.width.in_mm();

			const topRightPerimeter = this.get_corner_perimeter( this.top_right_radius.in_mm() );
			const bottomRightPerimeter = this.get_corner_perimeter( this.bottom_right_radius.in_mm() );
			const bottomLeftPerimeter = this.get_corner_perimeter( this.bottom_left_radius.in_mm() );
			const topLeftPerimeter = this.get_corner_perimeter( this.top_left_radius.in_mm() );

			const cornerPerimeter = topRightPerimeter + bottomRightPerimeter + bottomLeftPerimeter + topLeftPerimeter;

			const rectangleTop = width - this.top_right_radius.in_mm() - this.top_left_radius.in_mm();
			const rectangleBottom = width - this.bottom_right_radius.in_mm() - this.bottom_left_radius.in_mm();
			const rectangleLeft = height - this.top_left_radius.in_mm() - this.bottom_left_radius.in_mm();
			const rectangleRight = height - this.top_right_radius.in_mm() - this.bottom_right_radius.in_mm();

			const rectanglePerimeter = rectangleTop + rectangleBottom + rectangleLeft + rectangleRight;

			const perimeter = rectanglePerimeter + cornerPerimeter;

			return new Length( perimeter );
		}

		//p = 2 (a + b + π r)
		const r = this.radius.in_mm();
		const a = this.length.in_mm() - ( 2 * r );
		const b = this.width.in_mm() - ( 2 * r );
		return new Length( 2 * ( a + b + Math.PI * r ) );
	}

	/**
	 * Calculates the perimeter of a quarter circle based on the given radius.
	 *
	 * @param {float} radius
	 * @returns {float} The perimeter of a quarter circle with the given radius.
	 */
	get_corner_perimeter( radius ) {
		const perimeter_circle = ( radius * 2 ) * Math.PI;
		return perimeter_circle / 4;
	}

	get_length() {
		return this.length;
	}

	get_width() {
		return this.width;
	}

	get_radius() {
		return this.radius;
	}

	/**
	 * Checks if the corner radii are all the same.
	 *
	 * @returns {boolean} false if all corners have the same radius.
	 */
	is_individual_radius() {
		return (
			this.top_left_radius.in_mm() !== this.top_right_radius.in_mm() ||
			this.top_left_radius.in_mm() !== this.bottom_left_radius.in_mm() ||
			this.top_left_radius.in_mm() !== this.bottom_right_radius.in_mm()
		);
	}

	get_maximum_dimensions( maximum_rectangle ) {
		const smallest_length = Math.min( maximum_rectangle.get_length().in_mm(), maximum_rectangle.get_width().in_mm() );
		return {
			length: maximum_rectangle.get_length(),
			width: maximum_rectangle.get_width(),
			radius: new Length( smallest_length / 2 )
		};
	}

	get_dimension_keys() {
		return ["length", "width", "radius"];
	}

	get_modifier_keys() {
		return [];
	}

	serialize_primitives() {
		return {
			length: this.length.in_mm(),
			width: this.width.in_mm(),
			radius: this.radius.in_mm()
		};
	}

	static deserialize_primitives( primitives ) {
		return new RoundedRectangle(
			new Length( primitives.length ),
			new Length( primitives.width ),
			new Length( primitives.radius ),
			new Length( primitives.top_left_radius ),
			new Length( primitives.top_right_radius ),
			new Length( primitives.bottom_left_radius ),
			new Length( primitives.bottom_right_radius )
		);
	}

}
